// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// Admin API for PLATFORM-scope Quote Terms (provider rate cards).
// See docs/specs/quotes-engine-prd.md §7.1. Backed by /api/quotes/admin/terms/**.

export interface QuoteTermAllocationBand {
  id?: string;
  contributionMin?: number | null;
  contributionMax?: number | null;
  allocationRate: number;
  sortOrder?: number;
}

export interface QuoteTermOption {
  id?: string;
  optionCode: string;
  optionName?: string | null;
  sortOrder?: number;
  allocationBasis: 'GROSS' | 'NET' | 'FLAT';
  flatAllocationRate?: number | null;
  standardFundCharge?: number | null;
  passiveFundCharge?: number | null;
  fixedAllocationFundCharge?: number | null;
  initialCommissionMin?: number | null;
  initialCommissionMax?: number | null;
  initialCommissionFixed?: number | null;
  fundBasedCommissionMin?: number | null;
  fundBasedCommissionMax?: number | null;
  accrualRate?: string | null;
  minContribution?: number | null;
  maxContribution?: number | null;
  minTermYears?: number | null;
  exitCharges?: number[];
  commissionClawback?: number[];
  policyFeeAmount?: number | null;
  policyFeeFrequency?: string | null;
  fundSwitches?: string | null;
  customerWithdrawals?: string | null;
  maxNraForCommission?: number | null;
  metadata?: Record<string, unknown> | null;
  bands?: QuoteTermAllocationBand[];
}

export interface QuoteTerm {
  id?: string;
  scope?: string;
  brokerageId?: string | null;
  providerId: string;
  providerName?: string;
  providerLogoUrl?: string;
  productId: string;
  productName?: string;
  category?: string;
  currencyCode: string;
  isActive?: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  notes?: string | null;
  options: QuoteTermOption[];
  createdAt?: string;
  updatedAt?: string;
}

const base = '/api/quotes/admin/terms';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface QuoteTermSeedResult {
  created: number;
  skipped: number;
  considered: number;
}

export const quoteTermApi = {
  list: (providerId: string): Promise<QuoteTerm[]> =>
    request(`${base}/providers/${providerId}`),
  seedDefaults: (): Promise<QuoteTermSeedResult> =>
    request(`${base}/seed-defaults`, { method: 'POST' }),
  get: (termId: string): Promise<QuoteTerm> =>
    request(`${base}/${termId}`),
  create: (body: QuoteTerm): Promise<QuoteTerm> =>
    request(base, { method: 'POST', body: JSON.stringify(body) }),
  update: (termId: string, body: QuoteTerm): Promise<QuoteTerm> =>
    request(`${base}/${termId}`, { method: 'PUT', body: JSON.stringify(body) }),
  activate: (termId: string): Promise<QuoteTerm> =>
    request(`${base}/${termId}/activate`, { method: 'POST' }),
  deactivate: (termId: string): Promise<QuoteTerm> =>
    request(`${base}/${termId}/deactivate`, { method: 'POST' }),
  remove: (termId: string): Promise<void> =>
    request(`${base}/${termId}`, { method: 'DELETE' }),
};
